import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
	ViewChild,
} from '@angular/core';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	fromEvent,
	map,
	tap,
} from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'app-search-box',
	templateUrl: './search-box.component.html',
	styleUrls: ['./search-box.component.scss'],
})
export class SearchBoxComponent implements OnInit, OnDestroy, AfterViewInit {
	private subs = new SubSink();

	@ViewChild('searchInput', { static: true })
	searchInputRef!: ElementRef<HTMLInputElement>;

	@Input() delay = 350;
	@Input() isLoading = false;
	@Input() placeholder = 'Pesquisar...';
	@Input() showSearchButton = true;
	@Input() size: 'sm' | 'md' | 'lg' = 'md';
	@Input() value = '';

	@Output() blurSearchEvent = new EventEmitter<Event>();
	@Output() changeSearchEvent = new EventEmitter<string>();
	@Output() clickSearchEvent = new EventEmitter<string>();
	@Output() focusSearchEvent = new EventEmitter<Event>();
	@Output() searchEvent = new EventEmitter<string>();

	styles = {
		buttonColor: 'p-button-primary',
		buttonSize: 'p-button',
		inputSize: 'p-inputtext',
	};

	ngOnInit() {
		this.initStyles();
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	ngAfterViewInit() {
		this.subs.add(
			fromEvent(this.searchInputRef.nativeElement, 'keyup')
				.pipe(
					filter((event: any) => event instanceof KeyboardEvent),
					filter(event => this.ignoreSpecialKeys(event)),
					map(event => {
						const input = event.target as HTMLInputElement;

						if (event.key === 'Escape') {
							this.isLoading = false;
							this.searchInputRef.nativeElement.blur();
							return null;
						}

						if (event.key === 'Enter') {
							this.isLoading = false;
							this.clickSearchEvent.emit(input.value);
							return null;
						}

						return input.value;
					}),
					filter(value => value !== null),
					tap(() => (this.isLoading = true)),
					debounceTime(this.delay),
					distinctUntilChanged()
				)
				.subscribe(value => {
					this.isLoading = false;
					this.searchEvent.emit(value as string);
				})
		);
	}

	get buttonStylesString() {
		const { buttonSize, buttonColor } = this.styles;
		return `${buttonSize} ${buttonColor}`;
	}

	get inputStylesString() {
		const { inputSize } = this.styles;
		return `${inputSize}`;
	}

	private initStyles() {
		const sizeMap: Record<typeof this.size, Partial<typeof this.styles>> = {
			sm: {
				buttonSize: 'p-button-sm',
				inputSize: 'p-inputtext-sm',
			},
			md: {
				buttonSize: 'p-button',
				inputSize: 'p-inputtext',
			},
			lg: {
				buttonSize: 'p-button-lg',
				inputSize: 'p-inputtext-lg',
			},
		};

		this.styles = { ...this.styles, ...(sizeMap[this.size] || sizeMap.md) };
	}

	private ignoreSpecialKeys(event: KeyboardEvent) {
		const specialKeys = ['Alt', 'Control', 'Shift'];
		return !specialKeys.includes(event.key);
	}

	onBlurEvent(event: Event) {
		this.blurSearchEvent.emit(event);
	}

	onClickEvent() {
		if (!this.value) return;

		this.clickSearchEvent.emit(this.value);
	}

	onChangeEvent() {
		this.changeSearchEvent.emit(this.value);
	}

	onFocusEvent(event: Event) {
		this.focusSearchEvent.emit(event);
	}
}
